import { getMonthLabel } from './getMonthLabel';
import { getMonthsByYear } from './getMonthsByYear';
import { getYearsDescending } from './getYearsDescending';

// Último mês com dados de um ano: o ano corrente é limitado ao mês atual,
// da mesma forma que as opções do filtro de competência.
export function getLastMonthOfYear(year) {
    const monthsCount = getMonthsByYear(year, true).length;

    return `${monthsCount}`.padStart(2, '0');
}

// Intervalo de dados disponível, derivado das mesmas funções que montam as
// opções dos filtros, para que exibição e seleção nunca divirjam.
export function getAvailablePeriod() {
    const years = getYearsDescending();
    const firstYear = years[0];
    const lastYear = years[years.length - 1];

    return {
        start: { year: firstYear, month: '01' },
        end: { year: lastYear, month: getLastMonthOfYear(lastYear) }
    };
}

export function formatPeriod({ year, month }) {
    return `${getMonthLabel(month)}/${year}`;
}

// Nomes de competência usados pelos endpoints dos dashboards. Cada resposta
// traz o período em um desses formatos, então a extração é feita por nome de
// campo e não por dashboard.
const YEAR_KEYS = ['ano', 'year', 'ano_competencia', 'ano_referencia'];
const MONTH_KEYS = [
    'mes',
    'month',
    'mes_competencia',
    'mes_competencia_numero',
    'mes_referencia'
];
const YEAR_MONTH_KEYS = ['anomes_s', 'anomes', 'competencia', 'mes_ano_competencia'];

const normalizeYear = (value) => {
    const year = `${value ?? ''}`.trim();

    return /^\d{4}$/.test(year) ? year : null;
};

const normalizeMonth = (value) => {
    const month = Number(`${value ?? ''}`.trim());

    if (!Number.isInteger(month) || month < 1 || month > 12) {
        return null;
    }

    return `${month}`.padStart(2, '0');
};

// 'AAAAMM' (anomes_s) ou 'MM/AAAA' (mes_ano_competencia).
const parseYearMonth = (value) => {
    const raw = `${value ?? ''}`.trim();

    if (/^\d{6}$/.test(raw)) {
        return { year: raw.slice(0, 4), month: normalizeMonth(raw.slice(4)) };
    }

    const slashed = raw.match(/^(\d{1,2})[/-](\d{4})$/);

    if (slashed) {
        return { year: slashed[2], month: normalizeMonth(slashed[1]) };
    }

    return null;
};

// Chave de um ponto do período: 'AAAA' quando a linha só informa o ano e
// 'AAAAMM' quando informa a competência completa.
const getPeriodKey = (row) => {
    if (!row || typeof row !== 'object') {
        return null;
    }

    const yearKey = YEAR_KEYS.find((key) => normalizeYear(row[key]));

    if (yearKey) {
        const year = normalizeYear(row[yearKey]);
        const monthKey = MONTH_KEYS.find((key) => normalizeMonth(row[key]));

        return monthKey ? `${year}${normalizeMonth(row[monthKey])}` : year;
    }

    const yearMonthKey = YEAR_MONTH_KEYS.find((key) => parseYearMonth(row[key]));

    if (yearMonthKey) {
        const { year, month } = parseYearMonth(row[yearMonthKey]);

        return month ? `${year}${month}` : year;
    }

    return null;
};

// Respostas pivotadas usam a própria competência como chave: { '2024': total }
// ou { '2024': { '01': total } }.
const isYearKey = (key) => /^(19|20)\d{2}$/.test(`${key}`);

const getMonthKeys = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return [];
    }

    return Object.keys(value).map(normalizeMonth).filter(Boolean);
};

// Percorre a resposta do backend — lista de linhas, objeto com várias listas
// ({ anualEvolution, monthEvolution }) ou mapa pivotado por competência
// ({ serie_mensal: { '2024': { '01': total } } }) — coletando as competências.
export function extractPeriodKeys(data, keys = new Set(), depth = 0) {
    if (!data || depth > 3) {
        return keys;
    }

    if (Array.isArray(data)) {
        data.forEach((item) => extractPeriodKeys(item, keys, depth + 1));

        return keys;
    }

    if (typeof data !== 'object') {
        return keys;
    }

    const key = getPeriodKey(data);

    if (key) {
        keys.add(key);

        return keys;
    }

    Object.entries(data).forEach(([property, value]) => {
        if (isYearKey(property)) {
            const months = getMonthKeys(value);

            if (months.length) {
                months.forEach((month) => keys.add(`${property}${month}`));
            } else {
                keys.add(`${property}`);
            }

            return;
        }

        if (value && typeof value === 'object') {
            extractPeriodKeys(value, keys, depth + 1);
        }
    });

    return keys;
}

// Uma resposta sem linha alguma é a única evidência de que não há dado no
// escopo consultado. Resposta com linhas mas sem competência não diz nada
// sobre o período, então não conta como ausência de dados.
export function hasAnyRows(data, depth = 0) {
    if (data === null || data === undefined || data === '') {
        return false;
    }

    // Abaixo do limite de recursão o conteúdo não é inspecionado; assumir que
    // há dado evita anunciar "sem dados" por engano.
    if (depth > 3) {
        return true;
    }

    if (Array.isArray(data)) {
        return data.length > 0;
    }

    if (typeof data !== 'object') {
        return true;
    }

    return Object.values(data).some((value) => hasAnyRows(value, depth + 1));
}

// Converte as competências coletadas no intervalo exibido. Linhas que só têm o
// ano não estreitam os meses: o ano corrente é limitado ao mês atual e os
// anteriores a dezembro.
export function getPeriodFromKeys(periodKeys) {
    const keys = [...periodKeys];

    if (keys.length === 0) {
        return null;
    }

    const years = keys.map((key) => key.slice(0, 4));
    const startYear = years.reduce((min, year) => (year < min ? year : min));
    const endYear = years.reduce((max, year) => (year > max ? year : max));

    const monthsOf = (year) =>
        keys
            .filter((key) => key.length === 6 && key.startsWith(year))
            .map((key) => key.slice(4));

    const startMonths = monthsOf(startYear);
    const endMonths = monthsOf(endYear);

    return {
        start: {
            year: startYear,
            month: startMonths.length
                ? startMonths.reduce((min, month) => (month < min ? month : min))
                : '01'
        },
        end: {
            year: endYear,
            month: endMonths.length
                ? endMonths.reduce((max, month) => (month > max ? month : max))
                : getLastMonthOfYear(endYear)
        }
    };
}
